import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./ListingDetail.css";
import { createPortal } from "react-dom";

const ListingDetail = () => {
  const { listingId } = useParams();
  const location = useLocation();

  const [listing, setListing] = useState(location.state?.listing || null);
  const [loading, setLoading] = useState(!location.state?.listing);
  const [selectedImage, setSelectedImage] = useState("");
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  useEffect(() => {
    if (listing) return;

    const fetchListings = async () => {
      try {
        const response = await fetch(
          "https://t3yaaqetsktr267ko4xucsactu0wdogd.lambda-url.us-east-2.on.aws/",
          {
            headers: { "x-custom-key": "crazySecretKey82183b48921$@^JNF@!" },
          }
        );

        const data = await response.json();

        let allListings = [];
        if (Array.isArray(data)) {
          allListings = data;
        } else if (data.listings && Array.isArray(data.listings)) {
          allListings = data.listings;
        }

        const found = allListings.find((item) => {
          const rentalUrl = item.RentalApplicationUrl || "";
          const queryString = rentalUrl.split("?")[1] || "";
          const params = new URLSearchParams(queryString);
          return params.get("listingId") === listingId;
        });

        setListing(found || null);
      } catch (error) {
        console.error("Error fetching listing detail:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, [listing, listingId]);

  const cleanDescription = (desc = "") =>
    desc.replace(/^Residential:/, "").trim() || "N/A";

  const formatCurrency = (value) => {
    if (!value) return "N/A";
    return `$${Number(value).toLocaleString()}`;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return dateValue;
    return date.toLocaleDateString();
  };

  const normalizeCount = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";

    if (typeof value === "number") return value;

    const stringValue = String(value).trim();

    const directNumber = stringValue.match(/\d+/);
    if (directNumber) return directNumber[0];

    const wordToNumber = {
      studio: 0,
      one: 1,
      two: 2,
      three: 3,
      four: 4,
      five: 5,
      six: 6,
      seven: 7,
    };

    const lowered = stringValue.toLowerCase();

    for (const [word, number] of Object.entries(wordToNumber)) {
      if (lowered.includes(word)) {
        return number;
      }
    }

    return "N/A";
  };

  const normalizeSqft = (value) => {
    if (value === null || value === undefined || value === "") return null;

    const stringValue = String(value).trim();

    if (
      stringValue === "0" ||
      stringValue.toLowerCase() === "n/a" ||
      stringValue.toLowerCase() === "na"
    ) {
      return null;
    }

    return stringValue;
  };

  const images = useMemo(() => {
    if (!listing) return [];

    const unitFiles = listing.Unit?.Files || [];
    const propertyFiles = listing.Property?.Files || [];

    const allImageFiles = [...unitFiles, ...propertyFiles].filter(
      (file) => file.Type === "Image" && file.Url
    );

    const uniqueImages = Array.from(
      new Map(allImageFiles.map((img) => [img.Url, img])).values()
    );

    return uniqueImages;
  }, [listing]);

  useEffect(() => {
    if (images.length > 0 && !selectedImage) {
      setSelectedImage(images[0].Url);
    }
  }, [images, selectedImage]);

  const openPhotoModal = (startIndex = 0) => {
    setModalImageIndex(startIndex);
    setIsPhotoModalOpen(true);
  };

  const closePhotoModal = () => {
    setIsPhotoModalOpen(false);
  };

  const goToNextImage = () => {
    if (images.length === 0) return;
    setModalImageIndex((prev) => (prev + 1) % images.length);
  };

  const goToPrevImage = () => {
    if (images.length === 0) return;
    setModalImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  useEffect(() => {
    if (!isPhotoModalOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closePhotoModal();
      } else if (e.key === "ArrowRight") {
        goToNextImage();
      } else if (e.key === "ArrowLeft") {
        goToPrevImage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isPhotoModalOpen, images.length]);

  if (loading) {
    return <div className="listing-detail-loading">Loading listing...</div>;
  }

  if (!listing) {
    return <div className="listing-detail-loading">Listing not found.</div>;
  }

  const unit = listing.Unit || {};
  const property = listing.Property || {};

  const address = `${unit.Address?.AddressLine1 || property.Address?.AddressLine1 || ""}, ${
    unit.Address?.City || property.Address?.City || ""
  }, ${unit.Address?.State || property.Address?.State || ""} ${
    unit.Address?.PostalCode || property.Address?.PostalCode || ""
  }`;

  const description =
    cleanDescription(unit.Description) ||
    cleanDescription(property.StructureDescription);

  const bedCount = normalizeCount(unit.UnitBedrooms || listing.UnitBedrooms);
  const bathCount = normalizeCount(unit.UnitBathrooms || listing.UnitBathrooms);
  const sqft = normalizeSqft(unit.UnitSize || listing.UnitSize);
  const rent = unit.MarketRent || listing.Rent || null;

  const rentalUrl = listing.RentalApplicationUrl || "";
  const publicUrl = listingId
    ? `https://deltapmg.managebuilding.com/Resident/rental-application/rfi?listingId=${listingId}`
    : "#";

  const features = [
    ...(unit.Amenities || []),
    ...(property.Amenities || []),
    ...(listing.Amenities || []),
  ];

  const uniqueFeatures = Array.from(new Set(features.filter(Boolean)));
  const currentModalImage = images[modalImageIndex]?.Url || "";

  return (
    <>
      <div className="listing-detail-page">
        <Helmet>
          <title>{address} | Delta PMG</title>
          <meta name="description" content={description} />
        </Helmet>

        <div className="listing-detail-wrapper">
          <div className="listing-detail-main">
            <div className="listing-gallery-column">
              <div className="listing-main-image">
                {selectedImage ? (
                  <img
                    src={selectedImage}
                    alt={address}
                    onClick={() =>
                      openPhotoModal(
                        Math.max(
                          images.findIndex((img) => img.Url === selectedImage),
                          0
                        )
                      )
                    }
                  />
                ) : (
                  <div className="listing-no-image">No Image Available</div>
                )}
              </div>

              {images.length > 0 && (
                <>
                  <div className="listing-thumbnails">
                    {images.slice(0, 5).map((img, index) => (
                      <button
                        key={img.Url || index}
                        className={`thumbnail-btn ${
                          selectedImage === img.Url ? "active" : ""
                        }`}
                        onClick={() => setSelectedImage(img.Url)}
                        type="button"
                      >
                        <img src={img.Url} alt={`Thumbnail ${index + 1}`} />
                      </button>
                    ))}
                  </div>

                  <div className="all-photos-row">
                    <button
                      type="button"
                      className="all-photos-btn"
                      onClick={() =>
                        openPhotoModal(
                          Math.max(
                            images.findIndex((img) => img.Url === selectedImage),
                            0
                          )
                        )
                      }
                    >
                      All Photos
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="listing-info-column">
              <h1>{address}</h1>

              <p className="listing-price">{formatCurrency(rent)} / month</p>
              <p className="listing-available">
                <strong>Available:</strong> {formatDate(listing.AvailableDate)}
              </p>

              <div className="listing-stats">
                <div>
                  <strong>{bedCount}</strong>
                  <span>Bed</span>
                </div>

                <div>
                  <strong>{bathCount}</strong>
                  <span>Bath</span>
                </div>

                {sqft && (
                  <div>
                    <strong>{sqft}</strong>
                    <span>Sqft</span>
                  </div>
                )}
              </div>

              <div className="listing-section">
                <h2>Description</h2>
                <p>{description}</p>
              </div>

              {uniqueFeatures.length > 0 && (
                <div className="listing-section">
                  <h2>Features</h2>
                  <ul className="feature-list">
                    {uniqueFeatures.map((feature, index) => (
                      <li key={`${feature}-${index}`}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="listing-actions">
                <a
                  href={rentalUrl || publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="listing-btn primary"
                >
                  Click Here to Fill Out our Free Application for this Property
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isPhotoModalOpen &&
        createPortal(
          <div className="photo-modal-overlay" onClick={closePhotoModal}>
            <div
              className="photo-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="photo-modal-close"
                onClick={closePhotoModal}
                type="button"
                aria-label="Close photo gallery"
              >
                ×
              </button>

              {images.length > 1 && (
                <>
                  <button
                    className="photo-modal-nav photo-modal-prev"
                    onClick={goToPrevImage}
                    type="button"
                    aria-label="Previous photo"
                  >
                    ‹
                  </button>

                  <button
                    className="photo-modal-nav photo-modal-next"
                    onClick={goToNextImage}
                    type="button"
                    aria-label="Next photo"
                  >
                    ›
                  </button>
                </>
              )}

              <div className="photo-modal-image-wrap">
                {currentModalImage && (
                  <img
                    src={currentModalImage}
                    alt={`Listing photo ${modalImageIndex + 1}`}
                    className="photo-modal-image"
                  />
                )}
              </div>

              {images.length > 0 && (
                <div className="photo-modal-footer">
                    <div className="photo-modal-count">
                    {modalImageIndex + 1} / {images.length}
                    </div>

                    <div className="photo-modal-numbers">
                    {images.map((_, index) => (
                        <button
                        key={index}
                        className={`photo-modal-number-btn ${
                            modalImageIndex === index ? "active" : ""
                        }`}
                        onClick={() => setModalImageIndex(index)}
                        type="button"
                        aria-label={`Go to photo ${index + 1}`}
                        >
                        {index + 1}
                        </button>
                    ))}
                    </div>
                </div>
                )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ListingDetail;